/**
 * Typed client for every endpoint in docs/CONTRACT.md ("REST API").
 * - Cookie session (`credentials: 'include'`).
 * - Every non-GET request carries `X-APOS-CSRF: 1`.
 * - Non-2xx responses throw `ApiError` with the server's `{ error: { code, message } }`.
 */
import type {
  PlanningConditions, AccessEvidence, SceneBuilding, SceneObstacle, ShotPath, ShotPreferences, ShotSpot, TerrainGridJson,
  AircraftHistory, FleetAircraftOverview, FlightSummary, TrackedAircraft, PersistentObservation, UserPreferences,
  CalloutPrefs,
  VisibilityProfile,
  TrafficTarget,
  Aircraft,
  AircraftObservation,
  Airport,
  AircraftSourcesStatus,
  Alert,
  AlertRule,
  Broadcast,
  BroadcastPreset,
  Camera,
  CameraObservation,
  BlockedUser,
  CommsMessage,
  ReportReceipt,
  ReportRequest,
  SupportInfo,
  AccountSuspensionNotice,
  CrewBoardEntry,
  CrewObservation,
  EpochMs,
  GeneralWeather,
  Id,
  Marker,
  MarkerCategory,
  Person,
  MarkSubjectSnapshot,
  MediaClip,
  MetarReport,
  Mission,
  MissionAirport,
  MissionAirportRole,
  MissionBundle,
  MissionEvent,
  MissionInvite,
  MissionLocation,
  MissionMember,
  MissionRecord,
  MissionSummary,
  ProviderStatus,
  Role,
  RunSheetItem,
  ScenarioInfo,
  TafReport,
  User,
  WorldState,
} from '@apos/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Extra fields of the server's error object (e.g. `suspension` on 403 account_suspended). */
  readonly details: Record<string, unknown>;
  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
  /** True for fetch failures (offline, DNS, server down) rather than HTTP errors. */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** A user-presentable message for any thrown value. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message || `Request failed (${e.status})`;
  if (e instanceof Error) return e.message;
  return String(e);
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  query?: Query;
  signal?: AbortSignal;
}

let base = '';
/** Override the API origin (tests / native shells). Default: same origin. */
export function setApiBase(url: string): void {
  base = url.replace(/\/$/, '');
}

function buildUrl(path: string, query?: Query): string {
  let url = `${base}/api${path}`;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) url += `?${s}`;
  }
  return url;
}

let onUnauthorized: (() => void) | null = null;
/** Called on any 401 (except /auth/me and /auth/login) so the session store can reset. */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

/** Tell the session layer the server ended our session (e.g. WebSocket close 4401). */
export function signalUnauthorized(): void {
  onUnauthorized?.();
}

export const ACCOUNT_SUSPENDED = 'account_suspended';
/** The suspension notice carried by a 403 account_suspended error, if any. */
export function suspensionOf(e: unknown): AccountSuspensionNotice | null {
  if (!(e instanceof ApiError) || e.code !== ACCOUNT_SUSPENDED) return null;
  const n = e.details.suspension as AccountSuspensionNotice | undefined;
  return n && typeof n === 'object' ? n : null;
}

let onSuspended: ((notice: AccountSuspensionNotice | null) => void) | null = null;
/** Called on any 403 account_suspended (sign-in included) and on WebSocket close 4423. */
export function setSuspendedHandler(fn: ((notice: AccountSuspensionNotice | null) => void) | null): void {
  onSuspended = fn;
}
/** Tell the session layer the account was suspended; null = notice unknown (re-check with /auth/me). */
export function signalSuspended(notice: AccountSuspensionNotice | null = null): void {
  onSuspended?.(notice);
}

function httpError(status: number, data: unknown, statusText: string): ApiError {
  const raw = (data as { error?: Record<string, unknown> } | undefined)?.error;
  const { code, message, ...details } = raw && typeof raw === 'object' ? raw : {};
  const e = new ApiError(status, typeof code === 'string' ? code : `http_${status}`, typeof message === 'string' ? message : statusText || 'Request failed', details);
  if (status === 403 && e.code === ACCOUNT_SUSPENDED) signalSuspended(suspensionOf(e));
  return e;
}

export async function request<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET' && method !== 'HEAD') headers['X-APOS-CSRF'] = '1';
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    throw new ApiError(0, 'network', 'Network unavailable — check your connection.');
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    if (res.status === 401 && onUnauthorized && !path.startsWith('/auth/')) onUnauthorized();
    throw httpError(res.status, data, res.statusText);
  }
  return data as T;
}

/** POST a raw body (voice notes). Same CSRF / error handling as `request`. */
export async function requestRaw<T>(method: string, path: string, body: Blob, query?: Query): Promise<T> {
  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers: { Accept: 'application/json', 'X-APOS-CSRF': '1', 'Content-Type': (body.type || 'application/octet-stream').split(';')[0]! },
      credentials: 'include',
      body,
    });
  } catch {
    throw new ApiError(0, 'network', 'Network unavailable — check your connection.');
  }
  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = text;
  }
  if (!res.ok) {
    if (res.status === 401) onUnauthorized?.();
    throw httpError(res.status, data, res.statusText);
  }
  return data as T;
}

const get = <T>(path: string, query?: Query, signal?: AbortSignal) => request<T>('GET', path, undefined, { query, signal });
const post = <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {});
const patch = <T>(path: string, body: unknown) => request<T>('PATCH', path, body);
const put = <T>(path: string, body: unknown) => request<T>('PUT', path, body);
const del = (path: string) => request<void>('DELETE', path);

const m = (id: Id) => `/missions/${encodeURIComponent(id)}`;

// ───────────── Input shapes ─────────────

export interface RegisterInput {
  email: string;
  password: string;
  displayName: string;
  inviteCode?: string;
}
export interface MissionCreateInput {
  name: string;
  kind: Mission['kind'];
  windowStart: EpochMs;
  windowEnd: EpochMs;
  timezone: string;
  center: Mission['center'];
  notes?: string;
  frameRate?: Mission['frameRate'];
  code?: string;
  /** The organization this shoot belongs to; null = personal. Left out: the account's only organization, if it has exactly one. */
  organizationId?: string | null;
}
export type MissionPatch = Partial<Pick<Mission, 'name' | 'kind' | 'windowStart' | 'windowEnd' | 'timezone' | 'center' | 'notes' | 'frameRate' | 'code'>>;
export type AircraftInput = Omit<Aircraft, 'id' | 'missionId' | 'createdAt'>;
export type CameraInput = Omit<Camera, 'id' | 'missionId' | 'createdAt'>;
export type LocationInput = Omit<MissionLocation, 'id' | 'missionId' | 'createdAt'>;
/** Crew: someone on the call sheet (POST adds someone without the app; `name` only for them). */
export type PersonInput = Partial<Pick<Person, 'name' | 'phone' | 'job' | 'locationId' | 'notes'>>;
export type AlertRuleInput = Omit<AlertRule, 'id' | 'missionId' | 'createdAt'>;
export interface CrewLocationInput {
  lat: number;
  lon: number;
  accuracyM?: number | null;
  altM?: number | null;
  speedMps?: number | null;
  headingDeg?: number | null;
  t?: EpochMs;
  source?: CrewObservation['source'];
  /** Phone facing (°T) while likely aimed, its ± accuracy and when it was read (server time). Round 4. */
  facingDeg?: number | null;
  facingAccuracyDeg?: number | null;
  facingT?: EpochMs | null;
}
export type CameraObservationInput = Partial<Omit<CameraObservation, 'missionId' | 'cameraId' | 'receivedAt'>>;
export type AircraftObservationInput = Omit<AircraftObservation, 'missionId' | 'aircraftId' | 'receivedAt' | 'source' | 'quality'> &
  Partial<Pick<AircraftObservation, 'source' | 'quality'>>;
export interface MarkerInput {
  label: string;
  category: MarkerCategory;
  t?: EpochMs;
  lat?: number | null;
  lon?: number | null;
  aircraftId?: Id | null;
  cameraId?: Id | null;
  notes?: string;
  // ── rich marks (field UX §8) ──
  /** Outbox idempotency key: a repeat POST returns the stored mark (200). */
  clientId?: string;
  isHero?: boolean;
  tags?: string[];
  /** Slate text at press time ("14:32:07:12"). */
  tc?: string | null;
  tcSource?: 'TOD' | 'JAM';
  operatorLat?: number | null;
  operatorLon?: number | null;
  operatorAccuracyM?: number | null;
  subjectSnapshot?: MarkSubjectSnapshot | null;
}
export type MarkerPatch = Partial<Pick<MarkerInput, 't' | 'label' | 'category' | 'lat' | 'lon' | 'aircraftId' | 'cameraId' | 'notes' | 'isHero' | 'tags'>>;
export type AircraftReportKind = 'overhead' | 'departed' | 'landed' | 'position';
export type RunSheetInput = Partial<Pick<RunSheetItem, 'label' | 'scheduledT' | 'durationSec' | 'notes' | 'status' | 'order'>>;
export type MediaClipInput = Omit<MediaClip, 'id' | 'missionId' | 'createdAt'>;
export interface TrafficResponse {
  sources?:Array<{id:string;notes:string;enabled:boolean}>;
  t: EpochMs;
  targets: TrafficTarget[];
  disclaimer: string;
}
export interface WeatherResponse {
  metars: MetarReport[];
  tafs: TafReport[];
  general: GeneralWeather | null;
  generalSource?: string | null;
  generalReceivedAt?: EpochMs | null;
  fetchedAt: EpochMs;
}
export interface TelemetrySince {
  aircraftObs: MissionRecord['aircraftObs'];
  crewObs: MissionRecord['crewObs'];
  cameraObs: MissionRecord['cameraObs'];
}
export interface RangeQuery {
  from?: EpochMs;
  to?: EpochMs;
}
/** Shot Planner data sources as reported by the server (routes/spatial.ts). */
export interface SpatialSource {
  id: string;
  name: string;
  ok: boolean;
  count: number | null;
  asOf?: EpochMs | null;
}
export interface SpatialSceneResponse {
  terrain: TerrainGridJson | null;
  buildings: SceneBuilding[];
  obstacles: SceneObstacle[];
  airports: Airport[];
  sources: SpatialSource[];
  attribution: string[];
  reasons: string[];
  cls?: string;
  disclaimer?: string;
}
export interface SpatialSpotsResponse {
  spots: ShotSpot[];
  computedMs: number;
  sources: SpatialSource[];
  reasons: string[];
  cls?: string;
  disclaimer?: string;
}

// ───────────── Client ─────────────

export const api = {
  shotPlannerWeather: (airport: string, signal?: AbortSignal) => get<{ reports: MetarReport[] }>('/public-cameras/weather', { airport }, signal),
  trackedAircraft: {
    list: () => get<TrackedAircraft[]>('/aircraft'),
    create: (a: { label: string; registration?: string | null; icao24?: string | null; organizationId?: string | null }) => post<TrackedAircraft>('/aircraft', a),
    get: (id: string) => get<{ aircraft: TrackedAircraft; recording: boolean; error: string | null }>(`/aircraft/${encodeURIComponent(id)}`),
    update: (id: string, p: { label?: string; enabled?: boolean }) => patch<TrackedAircraft>(`/aircraft/${encodeURIComponent(id)}`, p),
    history: (id: string, before?: number) => get<AircraftHistory>(`/aircraft/${encodeURIComponent(id)}/history`, { before }),
    flightSummaries: (id: string, from: number, to?: number) => get<FlightSummary[]>(`/aircraft/${encodeURIComponent(id)}/flight-summaries`, { from, to }),
    observations: (id: string, from: number, to: number, after?: number, signal?: AbortSignal) =>
      get<{ points: PersistentObservation[]; nextAfter: number | null }>(`/aircraft/${encodeURIComponent(id)}/observations`, { from, to, after }, signal),
  },
  fleet: { overview: () => get<FleetAircraftOverview[]>('/fleet/overview') },
  preferences: {
    get: () => get<Partial<UserPreferences>>('/me/preferences'),
    update: (p: Partial<UserPreferences>) => patch<Partial<UserPreferences>>('/me/preferences', p),
  },
  profile: { update: (displayName: string) => patch<{ user: User }>('/me/profile', { displayName }) },
  // System
  health: () => get<{ ok: boolean; version: string; time: EpochMs }>('/health'),
  time: (signal?: AbortSignal) => get<{ serverTime: EpochMs }>('/time', undefined, signal),
  providers: (missionId?: string) => get<ProviderStatus[]>(`/providers${missionId ? `?missionId=${encodeURIComponent(missionId)}` : ''}`),

  auth: {
    register: (input: RegisterInput) => post<{ user: User; joinedMissionId?: string | null }>('/auth/register', input),
    login: (email: string, password: string) => post<{ user: User }>('/auth/login', { email, password }),
    token: (email: string, password: string, deviceName: string) =>
      post<{ token: string; user: User }>('/auth/token', { email, password, deviceName }),
    logout: () => post<void>('/auth/logout'),
    me: () => get<{ user: User }>('/auth/me'),
  },

  missions: {
    list: () => get<MissionSummary[]>('/missions'),
    archived: () => get<MissionSummary[]>('/missions/archived'),
    archive: (id: Id) => post<Mission>(`${m(id)}/archive`),
    restore: (id: Id) => post<Mission>(`${m(id)}/restore`),
    create: (input: MissionCreateInput) => post<MissionBundle>('/missions', input),
    get: (id: Id) => get<MissionBundle>(m(id)),
    update: (id: Id, p: MissionPatch) => patch<Mission>(m(id), p),
    remove: (id: Id) => del(m(id)),
    start: (id: Id) => post<Mission>(`${m(id)}/start`),
    end: (id: Id) => post<Mission>(`${m(id)}/end`),
    invite: (id: Id, body: { role: Exclude<Role, 'owner'>; expiresInHours?: number; maxUses?: number }) =>
      post<MissionInvite>(`${m(id)}/invites`, body),
    /** All invites for the mission, newest first (producer+; includes expired/used-up codes). */
    invites: (id: Id) => get<MissionInvite[]>(`${m(id)}/invites`),
    revokeInvite: (id: Id, code: string) => del(`${m(id)}/invites/${encodeURIComponent(code)}`),
    members: (id: Id) => get<MissionMember[]>(`${m(id)}/members`),
    updateMember: (id: Id, userId: Id, p: { role?: Role; callsign?: string; color?: string }) =>
      patch<MissionMember>(`${m(id)}/members/${encodeURIComponent(userId)}`, p),
    removeMember: (id: Id, userId: Id) => del(`${m(id)}/members/${encodeURIComponent(userId)}`),
    purgeCrewLocations: (id: Id) => post<{ deleted: number }>(`${m(id)}/purge-crew-locations`),
  },
  join: (code: string) => post<MissionBundle>('/join', { code }),

  // Mission configuration (producer+)
  aircraft: {
    create: (id: Id, a: AircraftInput) => post<Aircraft>(`${m(id)}/aircraft`, a),
    update: (id: Id, aircraftId: Id, p: Partial<AircraftInput>) => patch<Aircraft>(`${m(id)}/aircraft/${aircraftId}`, p),
    remove: (id: Id, aircraftId: Id) => del(`${m(id)}/aircraft/${aircraftId}`),
  },
  cameras: {
    create: (id: Id, c: CameraInput) => post<Camera>(`${m(id)}/cameras`, c),
    update: (id: Id, cameraId: Id, p: Partial<CameraInput>) => patch<Camera>(`${m(id)}/cameras/${cameraId}`, p),
    remove: (id: Id, cameraId: Id) => del(`${m(id)}/cameras/${cameraId}`),
  },
  people: {
    list: (id: Id) => get<Person[]>(`${m(id)}/people`),
    create: (id: Id, p: PersonInput & { name: string }) => post<Person>(`${m(id)}/people`, p),
    update: (id: Id, personId: Id, p: PersonInput) => patch<Person>(`${m(id)}/people/${personId}`, p),
    remove: (id: Id, personId: Id) => del(`${m(id)}/people/${personId}`),
    /** Someone added by name has joined with the app: merge them into that member. */
    link: (id: Id, personId: Id, userId: Id) => post<Person>(`${m(id)}/people/${personId}/link`, { userId }),
  },
  locations: {
    create: (id: Id, l: LocationInput) => post<MissionLocation>(`${m(id)}/locations`, l),
    update: (id: Id, locationId: Id, p: Partial<LocationInput>) => patch<MissionLocation>(`${m(id)}/locations/${locationId}`, p),
    remove: (id: Id, locationId: Id) => del(`${m(id)}/locations/${locationId}`),
  },
  plannedRoutes: {
    stage: (id: Id, aircraftId: Id, options: { requestId: string; expectedUpdatedAt: number; spacingNm: number; offsetNm: number; side: 'left' | 'right'; personId: Id | null; siteIndex: number }) =>
      post<MissionLocation[]>(`${m(id)}/aircraft/${encodeURIComponent(aircraftId)}/planned-route/staging-sites`, options),
    save: (id: Id, aircraftId: Id, route: { name: string; expectedUpdatedAt: number | null; waypoints: Array<{ name: string; lat: number; lon: number }> }) =>
      put<import('@apos/shared').PlannedFlightRoute>(`${m(id)}/aircraft/${encodeURIComponent(aircraftId)}/planned-route`, route),
    remove: (id: Id, aircraftId: Id, expectedUpdatedAt: number) => del(`${m(id)}/aircraft/${encodeURIComponent(aircraftId)}/planned-route?expectedUpdatedAt=${expectedUpdatedAt}`),
  },
  missionAirports: {
    set: (id: Id, airports: Array<{ icao: string; role: MissionAirportRole }>) =>
      put<MissionAirport[]>(`${m(id)}/airports`, { airports }),
  },
  alertRules: {
    create: (id: Id, r: AlertRuleInput) => post<AlertRule>(`${m(id)}/alert-rules`, r),
    update: (id: Id, ruleId: Id, p: Partial<AlertRuleInput>) => patch<AlertRule>(`${m(id)}/alert-rules/${ruleId}`, p),
    remove: (id: Id, ruleId: Id) => del(`${m(id)}/alert-rules/${ruleId}`),
    /** Merge the recommended rules for the mission's current aircraft/crew/sites (replace wipes first). */
    defaults: (id: Id, replace = false) => post<AlertRule[]>(`${m(id)}/alert-rules/defaults`, { replace }),
  },
  airports: {
    search: (q: string, signal?: AbortSignal) => get<Airport[]>('/airports/search', { q }, signal),
    near: (lat: number, lon: number, radiusNm: number) => get<Airport[]>('/airports/near', { lat, lon, radiusNm }),
    get: (icao: string) => get<Airport>(`/airports/${encodeURIComponent(icao)}`),
  },

  // Telemetry ingest
  setSharing: (id: Id, enabled: boolean) => post<MissionMember>(`${m(id)}/sharing`, { enabled }),
  postCrewLocation: (id: Id, p: CrewLocationInput) => post<void>(`${m(id)}/crew/location`, p),
  postCrewLocationBatch: (id: Id, points: CrewLocationInput[]) => post<void>(`${m(id)}/crew/location/batch`, { points }),
  postCameraObservation: (id: Id, cameraId: Id, o: CameraObservationInput) =>
    post<void>(`${m(id)}/cameras/${cameraId}/observations`, o),
  postAircraftObservation: (id: Id, aircraftId: Id, o: AircraftObservationInput) =>
    post<void>(`${m(id)}/aircraft/${aircraftId}/observations`, o),

  // Timeline & replay reads
  record: (id: Id, q: RangeQuery & { maxPointsPerTrack?: number } = {}, signal?: AbortSignal) =>
    get<MissionRecord>(`${m(id)}/record`, { ...q }, signal),
  state: (id: Id, t: EpochMs) => get<WorldState>(`${m(id)}/state`, { t }),
  telemetrySince: (id: Id, t: EpochMs, signal?: AbortSignal) => get<TelemetrySince>(`${m(id)}/telemetry/since`, { t }, signal),
  exportUrl: (id: Id) => buildUrl(`${m(id)}/export`),
  exportKmlUrl: (id: Id) => buildUrl(`${m(id)}/export.kml`),

  weather: (id: Id) => get<WeatherResponse>(`${m(id)}/weather`),
  /** Other (non-mission) air traffic around the mission: live picture, or reconstructed at `t`. */
  traffic: (id: Id, t?: EpochMs) => get<TrafficResponse>(`${m(id)}/traffic`, t === undefined ? undefined : { t }),
  trafficDetails: (id: Id, faFlightId: string) => get<{at:EpochMs;route:unknown;track:unknown}>(`${m(id)}/traffic/${encodeURIComponent(faFlightId)}/details`),

  markers: {
    list: (id: Id) => get<Marker[]>(`${m(id)}/markers`),
    create: (id: Id, mk: MarkerInput) => post<Marker>(`${m(id)}/markers`, mk),
    update: (id: Id, markerId: Id, p: MarkerPatch) => patch<Marker>(`${m(id)}/markers/${markerId}`, p),
    remove: (id: Id, markerId: Id) => del(`${m(id)}/markers/${markerId}`),
    /** Soft delete (MARK Undo). WS publishes `marker op:'delete'`. */
    undo: (id: Id, markerId: Id) => post<Marker>(`${m(id)}/markers/${markerId}/undo`),
    restore: (id: Id, markerId: Id) => post<Marker>(`${m(id)}/markers/${markerId}/restore`),
    /** Raw audio body (audio/webm|mp4|m4a|aac|wav, ≤ 3 MB, ≤ 30 s). */
    uploadVoice: (id: Id, markerId: Id, audio: Blob, durationSec: number) =>
      requestRaw<Marker>('POST', `${m(id)}/markers/${markerId}/voice`, audio, { durationSec: Math.max(0.1, Math.min(30, durationSec)) }),
    voiceUrl: (id: Id, markerId: Id) => buildUrl(`${m(id)}/markers/${markerId}/voice`),
  },
  runSheet: {
    list: (id: Id) => get<RunSheetItem[]>(`${m(id)}/runsheet`),
    create: (id: Id, item: RunSheetInput & { label: string }) => post<RunSheetItem>(`${m(id)}/runsheet`, item),
    update: (id: Id, itemId: Id, p: RunSheetInput) => patch<RunSheetItem>(`${m(id)}/runsheet/${itemId}`, p),
    remove: (id: Id, itemId: Id) => del(`${m(id)}/runsheet/${itemId}`),
    import: (id: Id, format: 'csv' | 'text', data: string, replace = false) =>
      post<{ imported: number; errors: Array<{ row?: number; message: string }>; items: RunSheetItem[] }>(`${m(id)}/runsheet/import`, { format, data, replace }),
    advance: (id: Id) => post<{ done: RunSheetItem | null; active: RunSheetItem | null }>(`${m(id)}/runsheet/advance`),
  },
  broadcasts: {
    list: (id: Id, q: RangeQuery = {}) => get<Broadcast[]>(`${m(id)}/broadcasts`, { ...q }),
    send: (id: Id, preset: BroadcastPreset, text?: string) => post<Broadcast>(`${m(id)}/broadcasts`, text ? { preset, text } : { preset }),
    /** Automatic delivery receipt (sent by the receiving client, no user action). */
    receipt: (id: Id, broadcastId: Id) => post<Broadcast>(`${m(id)}/broadcasts/${broadcastId}/receipt`),
    /** Optional "seen". */
    ack: (id: Id, broadcastId: Id) => post<Broadcast>(`${m(id)}/broadcasts/${broadcastId}/ack`),
  },
  crewBoard: (id: Id) => get<CrewBoardEntry[]>(`${m(id)}/crew-board`),
  aircraftSources: (id: Id, aircraftId: Id) => get<AircraftSourcesStatus>(`${m(id)}/aircraft/${aircraftId}/sources`),
  /** One-off "is it on public ADS-B right now?" check (works before the mission starts). */
  aircraftLookup: (id: Id, aircraftId: Id) =>
    get<{ icao24: string | null; checkedAt: number; sources: Array<{ id: string; name: string; seen: boolean; error: string | null; fix: { lat: number; lon: number; altFt: number | null; gsKt: number | null; ageSec: number } | null }> }>(
      `${m(id)}/aircraft/${aircraftId}/lookup`,
    ),
  /** Crew-reported fallback when there is no public position (never shown as live). */
  reportAircraft: (id: Id, aircraftId: Id, kind: AircraftReportKind, pos?: { lat: number; lon: number }) =>
    post<unknown>(`${m(id)}/aircraft/${aircraftId}/report`, pos ? { kind, ...pos } : { kind }),
  setShootLead: (id: Id, userId: Id, isShootLead: boolean) =>
    patch<MissionMember>(`${m(id)}/members/${encodeURIComponent(userId)}`, { isShootLead }),
  alerts: {
    list: (id: Id, q: RangeQuery = {}) => get<Alert[]>(`${m(id)}/alerts`, { ...q }),
    ack: (id: Id, alertId: Id) => post<Alert>(`${m(id)}/alerts/${alertId}/ack`),
  },
  events: {
    list: (id: Id, q: RangeQuery = {}) => get<MissionEvent[]>(`${m(id)}/events`, { ...q }),
  },
  media: {
    list: (id: Id) => get<MediaClip[]>(`${m(id)}/media`),
    create: (id: Id, c: Partial<MediaClipInput> & Pick<MediaClipInput, 'filename' | 'startT' | 'endT'>) =>
      post<MediaClip>(`${m(id)}/media`, c),
    update: (id: Id, clipId: Id, p: Partial<MediaClipInput>) => patch<MediaClip>(`${m(id)}/media/${clipId}`, p),
    remove: (id: Id, clipId: Id) => del(`${m(id)}/media/${clipId}`),
    import: (id: Id, format: 'csv' | 'json', data: string) =>
      post<{ imported: number; errors: Array<string | { row?: number; message: string }> }>(`${m(id)}/media/import`, { format, data }),
    at: (id: Id, t: EpochMs) => get<MediaClip[]>(`${m(id)}/media/at`, { t }),
  },
  comms: {
    list: (id: Id) => get<CommsMessage[]>(`${m(id)}/comms`),
    create: (id: Id, text: string) => post<CommsMessage>(`${m(id)}/comms`, { text }),
  },

  /** Content safety (App Store Guideline 1.2): reports, blocks and the support contact. */
  moderation: {
    report: (body: ReportRequest) => post<ReportReceipt>('/reports', body),
    blocks: () => get<BlockedUser[]>('/blocks'),
    block: (userId: Id) => post<BlockedUser>('/blocks', { userId }),
    unblock: (userId: Id) => del(`/blocks/${encodeURIComponent(userId)}`),
    support: () => get<SupportInfo>('/support'),
  },

  /** Spoken-update preferences (per user, server-stored). */
  calloutPrefs: {
    get: () => get<CalloutPrefs>('/me/callout-prefs'),
    set: (p: Partial<CalloutPrefs>) => put<CalloutPrefs>('/me/callout-prefs', p),
  },
  /** Terrain visibility profile for a viewer standing at lat/lon (null profile = no DEM there). */
  visibilityProfile: (id: Id, lat: number, lon: number, signal?: AbortSignal) =>
    get<{ profile: VisibilityProfile | null }>(`${m(id)}/terrain/visibility`, { lat: Number(lat.toFixed(5)), lon: Number(lon.toFixed(5)) }, signal),

  /** Shot Planner (VLP-2598). A cold scene can take minutes (public Overpass); pass a signal with a long timeout. */
  spatial: {
    conditions: (airport: string, signal?: AbortSignal) => get<PlanningConditions>('/spatial/conditions', { airport }, signal),
    access: (lat: number, lon: number, signal?: AbortSignal) => get<AccessEvidence>('/spatial/access', { lat, lon }, signal),
    scene: (lat: number, lon: number, radiusKm: number, signal?: AbortSignal) =>
      get<SpatialSceneResponse>('/spatial/scene', { lat: Number(lat.toFixed(5)), lon: Number(lon.toFixed(5)), radiusKm }, signal),
    spots: (body: { path: ShotPath; prefs: ShotPreferences; limit?: number; accessMode?: 'public' | 'all'; heightAglM?: number }, signal?: AbortSignal) =>
      request<SpatialSpotsResponse>('POST', '/spatial/spots', body, { signal }),
  },

  sim: {
    scenarios: () => get<ScenarioInfo[]>('/sim/scenarios'),
    demo: (scenarioId: string, mode: 'live' | 'historical') => post<MissionBundle>('/sim/demo', { scenarioId, mode }),
    start: (id: Id, scenarioId: string) => post<unknown>(`${m(id)}/sim/start`, { scenarioId }),
    stop: (id: Id) => post<unknown>(`${m(id)}/sim/stop`),
  },
};

export type Api = typeof api;
